<template>
  <div class="border-t border-slate-100 dark:border-slate-700 pt-3 mt-3">
    <!-- Header row -->
    <div class="flex items-center justify-between px-1 mb-2">
      <span class="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wide">
        Backup
      </span>
      <!-- Toggle switch melhorado -->
      <button
        :disabled="toggling"
        class="relative inline-flex items-center gap-2 focus:outline-none disabled:opacity-60"
        :title="backupEnabled ? 'Clique para desativar o backup desta conversa' : 'Clique para ativar o backup desta conversa'"
        @click="toggleBackup"
      >
        <!-- Label de estado -->
        <span
          class="text-xs font-semibold transition-colors"
          :class="backupEnabled
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-slate-400 dark:text-slate-500'"
        >
          {{ toggling ? '...' : (backupEnabled ? 'Ativo' : 'Inativo') }}
        </span>
        <!-- Switch track -->
        <span
          class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shadow-inner"
          :class="backupEnabled
            ? 'bg-emerald-500 dark:bg-emerald-600'
            : 'bg-slate-300 dark:bg-slate-600'"
        >
          <!-- Switch thumb -->
          <span
            class="inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200"
            :class="backupEnabled ? 'translate-x-6' : 'translate-x-1'"
          />
        </span>
      </button>
    </div>

    <!-- Actions when enabled -->
    <div v-if="backupEnabled" class="space-y-1.5">
      <!-- Manual backup button -->
      <button
        :disabled="creatingBackup"
        class="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50 transition-colors font-medium"
        @click="createBackup"
      >
        <span>{{ creatingBackup ? '⏳' : '💾' }}</span>
        {{ creatingBackup ? 'Fazendo backup...' : 'Fazer backup agora' }}
      </button>

      <!-- Recent backups -->
      <div v-if="recentBackups.length" class="space-y-1">
        <p class="text-[10px] text-slate-400 uppercase tracking-wide px-1">Recentes</p>
        <div
          v-for="backup in recentBackups"
          :key="backup.id"
          class="flex items-center justify-between px-2 py-1.5 rounded bg-slate-50 dark:bg-slate-800"
        >
          <div>
            <span
              class="text-[10px] font-medium"
              :class="statusTextClass(backup.status)"
            >
              {{ statusLabel(backup.status) }}
            </span>
            <span class="text-[10px] text-slate-400 ml-1.5">
              {{ formatDate(backup.created_at) }}
            </span>
          </div>
          <div class="flex items-center gap-1.5">
            <a
              v-if="backup.pdf_url"
              :href="backup.pdf_url"
              target="_blank"
              class="text-[10px] text-blue-500 hover:underline"
            >PDF</a>
            <a
              v-if="backup.json_url"
              :href="backup.json_url"
              target="_blank"
              class="text-[10px] text-blue-500 hover:underline"
            >JSON</a>
          </div>
        </div>
      </div>

      <!-- Success / error message -->
      <p
        v-if="message"
        class="text-xs px-1 py-1 rounded"
        :class="messageError
          ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
          : 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'"
      >
        {{ message }}
      </p>
    </div>

    <!-- Hint when disabled -->
    <p v-else class="text-xs text-slate-400 dark:text-slate-500 px-1">
      Ative para incluir esta conversa no backup diário e permitir backups manuais.
    </p>
  </div>
</template>

<script>
import backupAPI from '../../../api/backup';

export default {
  name: 'ConversationBackupPanel',
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    initialBackupEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      backupEnabled: this.initialBackupEnabled,
      toggling: false,
      creatingBackup: false,
      recentBackups: [],
      message: null,
      messageError: false,
    };
  },
  async created() {
    if (this.backupEnabled) {
      await this.loadRecentBackups();
    }
  },
  watch: {
    conversationId() {
      this.backupEnabled = this.initialBackupEnabled;
      this.recentBackups = [];
      if (this.backupEnabled) this.loadRecentBackups();
    },
  },
  methods: {
    async toggleBackup() {
      this.toggling = true;
      try {
        const { data } = await backupAPI.toggleConversationBackup(this.conversationId);
        this.backupEnabled = data.backup_enabled;
        if (this.backupEnabled) await this.loadRecentBackups();
      } catch (e) {
        this.showMessage('Erro ao alterar configuração de backup.', true);
      } finally {
        this.toggling = false;
      }
    },
    async createBackup() {
      this.creatingBackup = true;
      this.message = null;
      try {
        const { data } = await backupAPI.createConversationBackup(this.conversationId);
        this.recentBackups.unshift(data);
        this.showMessage('Backup iniciado! Será concluído em instantes.', false);
      } catch (e) {
        const err = e.response?.data?.error || 'Erro ao iniciar backup.';
        this.showMessage(err, true);
      } finally {
        this.creatingBackup = false;
      }
    },
    async loadRecentBackups() {
      try {
        const { data } = await backupAPI.getConversationBackups(this.conversationId);
        this.recentBackups = (data.backups || []).slice(0, 3);
      } catch (e) {
        // silently ignore
      }
    },
    showMessage(msg, isError) {
      this.message = msg;
      this.messageError = isError;
      setTimeout(() => { this.message = null; }, 4000);
    },
    statusLabel(status) {
      return { pending: '⏳ Pendente', processing: '🔄 Processando', completed: '✅ Concluído', failed: '❌ Falhou' }[status] || status;
    },
    statusTextClass(status) {
      return {
        pending:    'text-amber-600 dark:text-amber-400',
        processing: 'text-blue-600 dark:text-blue-400',
        completed:  'text-green-600 dark:text-green-400',
        failed:     'text-red-600 dark:text-red-400',
      }[status] || 'text-slate-500';
    },
    formatDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    },
  },
};
</script>
